import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Save, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

const FIELDS = ["label", "doc_type", "section", "scope", "requirement", "source", "rule_version", "valid_from", "valid_to", "last_reviewed_at", "warning_text"];

function RuleRow({ r, onSaved }) {
  const [f, setF] = useState({ ...r, conditions: JSON.stringify(r.conditions || {}) });
  const save = async () => {
    try {
      const body = { ...f, conditions: JSON.parse(f.conditions || "{}") };
      if (r.id) await api.put(`/admin/subcon/rules/${r.id}`, body); else await api.post("/admin/subcon/rules", body);
      toast.success("Gespeichert"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Ungültige Eingabe"); }
  };
  const inp = (k, w = "w-32") => <Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`h-8 ${w} text-xs`} data-testid={`rule-${f.key}-${k}`} />;
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`rule-row-${f.key || "new"}`}>
      <td className="p-2">{inp("key", "w-20")}</td><td className="p-2">{inp("label", "w-56")}</td><td className="p-2">{inp("doc_type", "w-44")}</td>
      <td className="p-2">{inp("section", "w-24")}</td><td className="p-2">{inp("scope", "w-24")}</td><td className="p-2">{inp("requirement", "w-28")}</td>
      <td className="p-2"><Textarea value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} className="h-16 w-56 font-mono text-[10px]" data-testid={`rule-${f.key}-conditions`} /></td>
      <td className="p-2">{inp("source", "w-36")}</td><td className="p-2">{inp("rule_version", "w-24")}</td><td className="p-2">{inp("valid_from", "w-24")}</td><td className="p-2">{inp("valid_to", "w-24")}</td><td className="p-2">{inp("last_reviewed_at", "w-24")}</td>
      <td className="p-2"><input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid={`rule-${f.key}-active`} /></td>
      <td className="p-2"><Button size="sm" onClick={save} className="h-8 rounded-full bg-navy text-white" data-testid={`rule-${f.key || "new"}-save`}><Save className="h-3.5 w-3.5" /></Button></td>
    </tr>
  );
}

function TemplateEditor() {
  const [tpl, setTpl] = useState(null);
  const [ver, setVer] = useState("");
  const load = () => api.get("/admin/subcon/template").then((r) => { setTpl(r.data.template); setVer(""); });
  useEffect(() => { load(); }, []);
  if (!tpl) return null;
  const upd = (i, k, v) => setTpl({ ...tpl, clauses: tpl.clauses.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  const save = async () => {
    try { await api.post("/admin/subcon/template", { version: ver, clauses: tpl.clauses, disclaimer: tpl.disclaimer }); toast.success("Neue Vorlagenversion aktiv"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="card-soft mt-8 p-6" data-testid="template-editor">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-lg font-bold text-navy">Vertragsvorlage · aktiv: {tpl.version}</h2>
        <Input value={ver} onChange={(e) => setVer(e.target.value)} placeholder="Neue Version, z. B. 2026-11.1" className="h-9 w-56" data-testid="template-version" />
        <Button disabled={!ver} onClick={save} className="rounded-full bg-cyan text-white" data-testid="template-save">Als neue Version speichern</Button>
      </div>
      <Textarea value={tpl.disclaimer || ""} onChange={(e) => setTpl({ ...tpl, disclaimer: e.target.value })} className="mb-4 text-xs" data-testid="template-disclaimer" />
      {tpl.clauses.map((c, i) => (
        <div key={c.key} className="mb-3 grid gap-2 border-b border-slate-100 pb-3 sm:grid-cols-[160px_1fr]" data-testid={`clause-${c.key}`}>
          <div className="space-y-1"><Input value={c.title} onChange={(e) => upd(i, "title", e.target.value)} className="h-8 text-xs" /><div className="text-[10px] text-slate-400">{c.key}{c.condition ? ` · ${c.condition}` : ""}</div></div>
          <Textarea value={c.text} onChange={(e) => upd(i, "text", e.target.value)} className="text-xs" data-testid={`clause-${c.key}-text`} />
        </div>
      ))}
    </div>
  );
}

export default function AdminSubconRules() {
  const [d, setD] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/subcon/rules").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <PageLoading />;
  const blank = { key: "", conditions: {}, active: true, scope: "COMPANY", requirement: "OPTIONAL", section: "COMPANY", ...Object.fromEntries(FIELDS.map((k) => [k, ""])) };
  return (
    <div data-testid="admin-subcon-rules">
      <div className="mb-2 flex items-center gap-2"><Scale className="h-6 w-6 text-cyan-dark" /><h1 className="font-heading text-2xl font-extrabold text-navy">Nachunternehmer-Regeln & Vorlage</h1></div>
      <p className="mb-6 max-w-3xl text-sm text-slate-500">Dokumentanforderungen pro Ansässigkeit ({d.establishments.join(", ")}), Mitarbeiterkategorie ({d.worker_categories.join(", ")}) und Entsendung. Bedingungen als JSON, z. B. {"{\"establishment\":[\"EU_EEA\"],\"posted\":true}"}. Abschnitte: {d.sections.join(", ")}.</p>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="text-left text-xs" data-testid="rules-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-400"><tr>{["Key", "Bezeichnung", "Dokumenttyp", "Abschnitt", "Scope", "Anforderung", "Bedingungen", "Quelle", "Regelversion", "Gültig ab", "Gültig bis", "Geprüft am", "Aktiv", ""].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
          <tbody>
            {d.rules.map((r) => <RuleRow key={r.id} r={r} onSaved={load} />)}
            {adding && <RuleRow r={blank} onSaved={() => { setAdding(false); load(); }} />}
          </tbody>
        </table>
      </div>
      {!adding && <Button variant="outline" onClick={() => setAdding(true)} className="mt-3 rounded-full" data-testid="rule-add"><Plus className="mr-1 h-4 w-4" /> Regel hinzufügen</Button>}
      <TemplateEditor />
    </div>
  );
}
